'use client';

import { useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, memo, useContext } from 'react';
import { useWatch } from 'react-hook-form';
import { CartContext } from '@/entities/cart';
import { CART_NOTE_MAX_LENGTH } from '@/shared/config/constants/notes';
import { cn } from '@/shared/lib/cn';
import { EventService } from '@/shared/lib/event-service';
import { isEmailValid } from '@/shared/lib/validations';
import { Card } from '@/shared/ui/card';
import { Form } from '@/shared/ui/form';
import './cart-customer-data-form-box.scss';
import { CartCustomerPhoneField } from './cart-customer-phone-field';

type Props = ComponentPropsWithoutRef<typeof Card>;
export const CartCustomerDataFormBox = memo(function CartCustomerDataFormBox(props: Props) {
    const t = useTranslations();

    const { formMethods } = useContext(CartContext);
    const isCompanyDataVisible = useWatch({ control: formMethods.control, name: 'addCompanyInfo' });

    return (
        <Card
            {...props}
            className={cn('cart-customer-data-form-box', props.className)}
            data-testid="cart-customer-data-form"
        >
            <h3
                className="cart-customer-data-form-box__title"
                data-testid="cart-customer-data-form-box-title"
            >
                {t('CART_CUSTOMER_DATA_FORM_BOX.TITLE')}
            </h3>
            <div
                className="cart-customer-data-form-box__fields"
                data-testid="cart-customer-data-form-box-fields"
            >
                <CartCustomerPhoneField />
                <Form.TextField
                    name="contactInfo.name"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.NAME_LABEL')}
                    rules={{ required: true }}
                    data-testid="name-input"
                />
                <Form.TextField
                    name="contactInfo.surname"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.SURNAME_LABEL')}
                    rules={{ required: true }}
                    data-testid="surname-input"
                />
                <Form.TextField
                    name="contactInfo.email"
                    type="email"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.EMAIL_LABEL')}
                    data-testid="email-input"
                    rules={{
                        required: true,
                        validate: (value) => isEmailValid(value) || t('ERRORS.INVALID_EMAIL'),
                    }}
                />
                <Form.TextArea
                    name="note"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.NOTE_LABEL')}
                    data-testid="note-textarea"
                    rules={{ maxLength: CART_NOTE_MAX_LENGTH }}
                />
                <Form.Checkbox
                    name="addCompanyInfo"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.USE_COMPANY_DATA_LABEL')}
                    data-testid="use-company-data-checkbox"
                />
                {isCompanyDataVisible && (
                    <>
                        <Form.TextField
                            name="company.name"
                            label={t('CART_CUSTOMER_DATA_FORM_BOX.COMPANY_NAME_LABEL')}
                            rules={{ required: true }}
                            data-testid="company-name-input"
                        />
                        <Form.AddressAutocompleteField
                            name="company.address"
                            fieldProps={{
                                label: t('CART_CUSTOMER_DATA_FORM_BOX.COMPANY_ADDRESS_LABEL'),
                            }}
                            rules={{ required: true }}
                            data-testid="company-address-input"
                        />
                        <Form.TextField
                            name="company.cin"
                            label={t('CART_CUSTOMER_DATA_FORM_BOX.COMPANY_CIN_LABEL')}
                            rules={{ required: true }}
                            data-testid="company-cin-input"
                        />
                        <Form.TextField
                            name="company.vat"
                            label={t('CART_CUSTOMER_DATA_FORM_BOX.COMPANY_VAT_LABEL')}
                            rules={{ required: true }}
                            data-testid="company-vat-input"
                        />
                        <Form.Checkbox
                            name="company.issueInvoice"
                            label={t('CART_CUSTOMER_DATA_FORM_BOX.COMPANY_INVOICE_LABEL')}
                            data-testid="company-invoice-checkbox"
                        />
                    </>
                )}
                <hr className="cart-customer-data-form-box__divider" />
                <Form.Checkbox
                    name="saveForNextOrder"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.SAVE_FOR_NEXT_ORDER_LABEL')}
                    data-testid="save-for-next-order-checkbox"
                />
                <Form.Checkbox
                    name="privacyPolicyAgreement"
                    data-testid="privacy-policy-agreement-checkbox"
                    label={
                        <p>
                            {t.rich('CART_CUSTOMER_DATA_FORM_BOX.TERMS_AGREEMENT_LABEL', {
                                terms: (chunks) => (
                                    <span
                                        className="cart-customer-data-form-box__link"
                                        role="button"
                                        data-testid="terms-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            EventService.trigger('terms-and-conditions:view-detail', {
                                                onAccept: () => {
                                                    formMethods.setValue('privacyPolicyAgreement', true);
                                                },
                                            });
                                        }}
                                    >
                                        {chunks}
                                    </span>
                                ),
                                privacy: (chunks) => (
                                    <span
                                        className="cart-customer-data-form-box__link"
                                        role="button"
                                        data-testid="privacy-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            EventService.trigger('privacy-policy:view-detail', {
                                                onAccept: () => {
                                                    formMethods.setValue('privacyPolicyAgreement', true);
                                                },
                                            });
                                        }}
                                    >
                                        {chunks}
                                    </span>
                                ),
                            })}
                        </p>
                    }
                    rules={{
                        required: true,
                    }}
                />
                <Form.Checkbox
                    name="newsletterAgreement"
                    label={t('CART_CUSTOMER_DATA_FORM_BOX.NEWS_AGREEMENT_LABEL')}
                    data-testid="newsletter-agreement-checkbox"
                />
            </div>
        </Card>
    );
});
